import React, { useEffect, useState } from 'react';

import { Button, Divider, Form, Input, notification, Space, Spin } from 'antd';

import airportService, {
  airportPageToFormFields,
  AirportParams,
} from '../../../services/map/airport-service';
import ErrorsType from '../../../typings/ErrorsType';
import TitledCard from '../../layout-components/titled-card';
import TextEditor from '../../layouts/text-editor';

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 14 },
};
const tailLayout = {
  wrapperCol: { offset: 4, span: 14 },
};

const AirportForm: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm<AirportParams>();
  const [errors, setErrors] = useState<Record<string, Array<string>>>({});

  useEffect(() => {
    airportService
      .getAirport()
      .then((response) => {
        const formFields = airportPageToFormFields(response.static_page);
        for (let inx = formFields.sections.length; inx < 3; inx += 1) {
          formFields.sections.push({
            id: undefined,
            title_pl: '',
            title_en: '',
            content_pl: '',
            content_en: '',
          });
        }
        form.setFieldsValue(formFields);
      })
      .catch((e: ErrorsType) => {
        if (e.statusCode === 404) {
          form.setFieldsValue({ sections: [{}, {}, {}] });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [form]);

  const onFinish = (values: AirportParams) => {
    setLoading(true);
    setErrors({});

    airportService
      .updateAirport({ ...values })
      .then((response) => {
        notification.success({
          message: 'Zapisano podstronę',
          description: (
            <>
              Dane podstrony <strong>Lotnisko Modlin</strong> zostały zaktualizowane.
            </>
          ),
        });
        const formFields = airportPageToFormFields(response.static_page);
        for (let inx = formFields.sections.length; inx < 3; inx += 1) {
          formFields.sections.push({
            id: undefined,
            title_pl: '',
            title_en: '',
            content_pl: '',
            content_en: '',
          });
        }
        form.setFieldsValue(formFields);
      })
      .catch((e: ErrorsType) => {
        if (e.statusCode === 422) {
          setErrors(e.errors || {});
        } else {
          // eslint-disable-next-line @typescript-eslint/no-throw-literal
          throw e;
        }
        notification.error({
          message: 'Dane nie mogły być zapisane',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onFinishFailed = () => {
    notification.error({
      message: 'Dane nie mogły być zapisane',
    });
  };

  return (
    <Spin spinning={loading}>
      <TitledCard title='Edycja podstrony "Lotnisko Modlin"'>
        <Form
          {...layout}
          form={form}
          name="technologyForm"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item
            label="Tytuł"
            name="title_pl"
            rules={[{ required: true, message: 'Tytuł jest wymagany' }]}
            hasFeedback
            help={errors.title_pl?.[0]}
            validateStatus={errors.title_pl ? 'error' : undefined}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Tytuł (EN)"
            name="title_en"
            rules={[{ required: true, message: 'Tytuł jest wymagany' }]}
            hasFeedback
            help={errors.title_en?.[0]}
            validateStatus={errors.title_en ? 'error' : undefined}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Identyfikator podstrony"
            name="slug"
            hasFeedback
            help={errors.slug?.[0]}
            validateStatus={errors.slug ? 'error' : undefined}
          >
            <Input readOnly />
          </Form.Item>

          <Form.List name="sections">
            {(fields) => (
              <>
                {fields.map((field) => (
                  <React.Fragment key={field.key}>
                    <Divider />
                    <Form.Item name={[field.name, 'id']} noStyle>
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item label="Treść sekcji (PL)" name={[field.name, 'content_pl']}>
                      <TextEditor />
                    </Form.Item>
                    <Form.Item label="Treść sekcji (EN)" name={[field.name, 'content_en']}>
                      <TextEditor />
                    </Form.Item>
                  </React.Fragment>
                ))}
              </>
            )}
          </Form.List>

          <Form.Item {...tailLayout}>
            <Space>
              <Button className="mr-2" type="primary" htmlType="submit">
                Zapisz
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </TitledCard>
    </Spin>
  );
};
export default AirportForm;
